// Draws — Sprint 1.5b: click any milestone row to update status, mark
// completed, attach SF-425 / draw-request PDF.

import { useMemo, useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import DocumentUploader from '../components/DocumentUploader';

type Project = { id: string; project_name: string; project_code: string };
type Obligation = {
  id: string; code: string; name: string; cadence_rule: string;
  source_cite: string | null; due_date: string;
  status: 'pending' | 'in_progress' | 'submitted' | 'accepted' | 'rejected' | 'overdue' | 'na';
};
type Milestone = {
  id: string; seq: number; name: string;
  draw_pct: number | null;
  target_date: string | null;
  completed_date: string | null;
  status: 'pending' | 'in_progress' | 'submitted' | 'approved' | 'rejected';
  evidence_required: string[] | null;
  notes: any;
};
type DrawsSummary = {
  project: {
    id: string; project_name: string;
    grant_amount_usd: number | null;
    match_amount_usd: number | null;
    total_project_cost_usd: number | null;
    federal_share_usd: number | null;
    match_share_usd: number | null;
  };
  milestones: Milestone[];
};

const STATUS_BADGE: Record<string, { bg: string; fg: string; label: string }> = {
  pending: { bg: '#f1f5f9', fg: '#475569', label: 'Pending' },
  in_progress: { bg: '#fef3c7', fg: '#92400e', label: 'In progress' },
  submitted: { bg: '#dbeafe', fg: '#1e40af', label: 'Submitted' },
  accepted: { bg: '#d1fae5', fg: '#065f46', label: 'Accepted' },
  approved: { bg: '#d1fae5', fg: '#065f46', label: 'Approved' },
  rejected: { bg: '#fee2e2', fg: '#991b1b', label: 'Rejected' },
  overdue: { bg: '#fecaca', fg: '#7f1d1d', label: 'Overdue' },
  na: { bg: '#e5e7eb', fg: '#6b7280', label: 'N/A' },
};

const fmtUSD = (n: number | null | undefined) => {
  if (n == null || !isFinite(Number(n))) return '—';
  const v = Number(n);
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(2)}M`;
  if (v >= 1_000) return `$${(v / 1_000).toFixed(0)}k`;
  return `$${Math.round(v).toLocaleString()}`;
};

function StatusBadge({ status }: { status: string }) {
  const s = STATUS_BADGE[status] ?? STATUS_BADGE.pending;
  return <span style={{ background: s.bg, color: s.fg, padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap' }}>{s.label}</span>;
}

function StatCard({ label, value, sub, color }: { label: string; value: string | number; sub?: string; color?: string }) {
  return (
    <div style={{ background: '#fff', border: '1px solid var(--border, #e2e8f0)', borderTop: `3px solid ${color ?? '#3b82f6'}`, borderRadius: 6, padding: '12px 14px' }}>
      <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--text-muted, #64748b)' }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 800, marginTop: 2 }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: 'var(--text-muted, #64748b)', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

export default function DrawsPage() {
  const [projectId, setProjectId] = useState<string | null>(null);
  const [editing, setEditing] = useState<Milestone | null>(null);

  const { data: projects } = useQuery<Project[]>({
    queryKey: ['bead-projects'],
    queryFn: () => api.get('/bead/projects'),
  });
  const activeProjectId = projectId ?? projects?.[0]?.id ?? null;

  const { data: obligations = [] } = useQuery<Obligation[]>({
    queryKey: ['fin-obligations', activeProjectId],
    queryFn: () => api.get(`/bead-pm/projects/${activeProjectId}/obligations?category=federal_financial`),
    enabled: !!activeProjectId,
  });

  const { data: draws } = useQuery<DrawsSummary>({
    queryKey: ['draws-summary', activeProjectId],
    queryFn: () => api.get(`/bead/projects/${activeProjectId}/draws-summary`),
    enabled: !!activeProjectId,
  });

  const drawObligations = useMemo(() =>
    obligations.filter(o =>
      ['FED-FIN-DRAW', 'FED-FIN-MATCH', 'FED-FIN-SF425', 'FED-FIN-CASH', 'FED-FIN-AUDIT', 'FED-FIN-EQUIP'].includes(o.code)
    ), [obligations]);

  const otherObligations = useMemo(() =>
    obligations.filter(o => !drawObligations.some(d => d.id === o.id)),
    [obligations, drawObligations]);

  const kpis = useMemo(() => {
    const ms = draws?.milestones ?? [];
    const completedDrawPct = ms
      .filter(m => m.status === 'approved' || m.completed_date)
      .reduce((acc, m) => acc + (Number(m.draw_pct) || 0), 0);
    const federal = Number(draws?.project?.federal_share_usd ?? draws?.project?.grant_amount_usd ?? 0);
    const match = Number(draws?.project?.match_share_usd ?? draws?.project?.match_amount_usd ?? 0);
    const total = Number(draws?.project?.total_project_cost_usd ?? federal + match);
    const drawnUsd = federal * (completedDrawPct / 100);
    return { totalMilestones: ms.length, completedDrawPct, federal, match, total, drawnUsd };
  }, [draws]);

  return (
    <div>
      <div className="page__header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div>
          <h1 className="page__title" style={{ margin: 0, fontSize: 24 }}>Draws</h1>
          <div className="page__subtitle" style={{ color: 'var(--text-muted, #64748b)', fontSize: 13 }}>
            Federal grant drawdowns, match documentation, and financial reporting
          </div>
        </div>
        <select value={activeProjectId ?? ''} onChange={(e) => setProjectId(e.target.value || null)}
          style={{ padding: '6px 10px', fontSize: 13, border: '1px solid var(--border, #e2e8f0)', borderRadius: 6 }}>
          {(projects ?? []).map(p => <option key={p.id} value={p.id}>{p.project_name} ({p.project_code})</option>)}
        </select>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12, marginBottom: 16 }}>
        <StatCard label="Federal share" value={fmtUSD(kpis.federal)} color="#3b82f6" />
        <StatCard label="Match share" value={fmtUSD(kpis.match)} color="#8b5cf6" />
        <StatCard label="Project total" value={fmtUSD(kpis.total)} color="#0f172a" />
        <StatCard label="Drawn to date" value={fmtUSD(kpis.drawnUsd)} sub={`${kpis.completedDrawPct.toFixed(1)}% of federal`} color="#10b981" />
        <StatCard label="Milestones" value={kpis.totalMilestones} sub={`${(draws?.milestones ?? []).filter(m => m.status === 'approved').length} approved`} color="#f59e0b" />
      </div>

      <section style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: 16, marginBottom: 8 }}>Draw schedule (milestones) · {(draws?.milestones ?? []).length}</h2>
        <div style={{ background: '#fff', border: '1px solid var(--border, #e2e8f0)', borderRadius: 6, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead style={{ background: '#f8fafc', borderBottom: '1px solid var(--border, #e2e8f0)' }}>
              <tr>{['#','Milestone','Draw %','Federal $','Target','Completed','Status'].map(h =>
                <th key={h} style={{ padding: '8px 10px', textAlign: h === 'Draw %' || h === 'Federal $' ? 'right' : 'left', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '.06em', color: '#64748b' }}>{h}</th>
              )}</tr>
            </thead>
            <tbody>
              {!draws || draws.milestones.length === 0 ? (
                <tr><td colSpan={7} style={{ padding: 16, color: '#94a3b8', textAlign: 'center' }}>No milestones defined for this project's award.</td></tr>
              ) : draws.milestones.map(m => (
                <tr key={m.id} onClick={() => setEditing(m)}
                  style={{ borderBottom: '1px solid #f1f5f9', cursor: 'pointer' }}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
                  <td style={{ padding: '8px 10px', color: '#94a3b8', fontFamily: 'ui-monospace, monospace' }}>{m.seq}</td>
                  <td style={{ padding: '8px 10px', fontWeight: 500 }}>{m.name}</td>
                  <td style={{ padding: '8px 10px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{m.draw_pct != null ? `${Number(m.draw_pct).toFixed(1)}%` : '—'}</td>
                  <td style={{ padding: '8px 10px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: '#64748b' }}>
                    {m.draw_pct != null ? fmtUSD(kpis.federal * Number(m.draw_pct) / 100) : '—'}
                  </td>
                  <td style={{ padding: '8px 10px', fontSize: 11, color: '#64748b' }}>{m.target_date ?? '—'}</td>
                  <td style={{ padding: '8px 10px', fontSize: 11, color: '#64748b' }}>{m.completed_date ?? '—'}</td>
                  <td style={{ padding: '8px 10px' }}><StatusBadge status={m.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
          {(draws?.milestones?.length ?? 0) > 0 && (
            <div style={{ padding: '6px 10px', fontSize: 11, color: '#64748b', borderTop: '1px solid var(--border)', background: '#f8fafc' }}>
              Click any milestone to mark complete, attach the SF-425 / draw-request PDF, or update target dates.
            </div>
          )}
        </div>
      </section>

      <section style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: 16, marginBottom: 8 }}>Draw-cycle obligations · {drawObligations.length}</h2>
        <div style={{ background: '#fff', border: '1px solid var(--border, #e2e8f0)', borderRadius: 6, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead style={{ background: '#f8fafc', borderBottom: '1px solid var(--border, #e2e8f0)' }}>
              <tr>{['Code','Requirement','Authority','Cadence','Due','Status'].map(h =>
                <th key={h} style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '.06em', color: '#64748b' }}>{h}</th>
              )}</tr>
            </thead>
            <tbody>
              {drawObligations.length === 0 ? (
                <tr><td colSpan={6} style={{ padding: 16, color: '#94a3b8', textAlign: 'center' }}>No draw-cycle obligations.</td></tr>
              ) : drawObligations.map(o => (
                <tr key={o.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                  <td style={{ padding: '8px 10px', fontFamily: 'ui-monospace, monospace', fontSize: 11, color: '#64748b' }}>{o.code}</td>
                  <td style={{ padding: '8px 10px', fontWeight: 500 }}>{o.name}</td>
                  <td style={{ padding: '8px 10px', fontSize: 11, color: '#64748b' }}>{o.source_cite ?? '—'}</td>
                  <td style={{ padding: '8px 10px', fontSize: 11, color: '#64748b' }}>{o.cadence_rule.replace(/_/g, ' ')}</td>
                  <td style={{ padding: '8px 10px', fontSize: 11, color: '#64748b' }}>{o.due_date ?? '—'}</td>
                  <td style={{ padding: '8px 10px' }}><StatusBadge status={o.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ padding: '6px 10px', fontSize: 11, color: '#64748b', borderTop: '1px solid var(--border)', background: '#f8fafc' }}>
            Update obligation status + attach evidence on <b>Compliance → Overview</b>.
          </div>
        </div>
      </section>

      {otherObligations.length > 0 && (
        <section>
          <h2 style={{ fontSize: 14, marginBottom: 8, color: '#64748b' }}>Other financial obligations · {otherObligations.length}</h2>
          <div style={{ background: '#fff', border: '1px solid var(--border, #e2e8f0)', borderRadius: 6, overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <tbody>
                {otherObligations.map(o => (
                  <tr key={o.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '6px 10px', fontFamily: 'ui-monospace, monospace', fontSize: 11, color: '#94a3b8' }}>{o.code}</td>
                    <td style={{ padding: '6px 10px', fontSize: 12 }}>{o.name}</td>
                    <td style={{ padding: '6px 10px', fontSize: 11, color: '#64748b' }}>{o.source_cite ?? '—'}</td>
                    <td style={{ padding: '6px 10px', fontSize: 11, color: '#64748b' }}>{o.due_date ?? '—'}</td>
                    <td style={{ padding: '6px 10px' }}><StatusBadge status={o.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {editing && activeProjectId && (
        <MilestoneEditor
          milestone={editing}
          projectId={activeProjectId}
          federalUsd={kpis.federal}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

// --------------------------------------------------------------------
// Milestone editor drawer
// --------------------------------------------------------------------

function MilestoneEditor({
  milestone, projectId, federalUsd, onClose,
}: { milestone: Milestone; projectId: string; federalUsd: number; onClose: () => void }) {
  const qc = useQueryClient();
  const [status, setStatus] = useState(milestone.status);
  const [completed, setCompleted] = useState(milestone.completed_date ?? '');
  const [target, setTarget] = useState(milestone.target_date ?? '');
  const [drawPct, setDrawPct] = useState(milestone.draw_pct != null ? String(milestone.draw_pct) : '');
  const [pendingDocId, setPendingDocId] = useState<string | null>(null);

  const saveMut = useMutation({
    mutationFn: (b: any) => api.put(`/bead/milestones/${milestone.id}`, b),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['draws-summary', projectId] }),
  });
  const linkAttMut = useMutation({
    // Find or create an obligation for FED-FIN-DRAW + attach. For now, file
    // the doc tagged to the project (no obligation linkage); a Sprint 2
    // refinement will route to the right obligation by code+period.
    mutationFn: (_id: string) => Promise.resolve(),
  });

  // Auto-save when uploader returns a doc; we don't yet link it to a
  // specific obligation here — it's stored under bead_document with the
  // project + milestone metadata so binder/audit can find it.
  useEffect(() => {
    if (pendingDocId) setPendingDocId(null);
  }, [pendingDocId]);

  function save() {
    const body: any = { status };
    if (completed !== (milestone.completed_date ?? '')) body.completed_date = completed || null;
    if (target !== (milestone.target_date ?? '')) body.target_date = target || null;
    const dp = drawPct === '' ? null : Number(drawPct);
    if (dp !== milestone.draw_pct) body.draw_pct = dp;
    // If marking approved, auto-fill completed_date if blank.
    if (status === 'approved' && !completed && milestone.completed_date == null) {
      body.completed_date = new Date().toISOString().slice(0, 10);
    }
    saveMut.mutate(body, { onSuccess: () => onClose() });
  }

  const computedFederal = drawPct ? federalUsd * Number(drawPct) / 100 : null;

  return (
    <div onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.4)', zIndex: 100, display: 'flex', justifyContent: 'flex-end' }}>
      <div onClick={(e) => e.stopPropagation()}
        style={{ width: 480, maxWidth: '100%', background: 'white', height: '100vh', overflowY: 'auto', padding: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
          <div>
            <div style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase' }}>Milestone {milestone.seq}</div>
            <div style={{ fontSize: 18, fontWeight: 700 }}>{milestone.name}</div>
            {computedFederal != null && (
              <div style={{ fontSize: 11, color: '#64748b' }}>Federal $ at {drawPct}% = {fmtUSD(computedFederal)}</div>
            )}
          </div>
          <button onClick={onClose} style={{ background: 'transparent', border: 0, fontSize: 24, cursor: 'pointer', color: '#64748b' }}>×</button>
        </div>

        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', marginBottom: 4 }}>Status</div>
          <select value={status} onChange={(e) => setStatus(e.target.value as any)}
            style={{ width: '100%', padding: '6px 10px', fontSize: 13, border: '1px solid var(--border)', borderRadius: 4 }}>
            <option value="pending">Pending</option>
            <option value="in_progress">In progress</option>
            <option value="submitted">Submitted (draw requested)</option>
            <option value="approved">Approved (drawn)</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 12 }}>
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', marginBottom: 4 }}>Target date</div>
            <input type="date" value={target} onChange={(e) => setTarget(e.target.value)}
              style={{ width: '100%', padding: '6px 10px', fontSize: 13, border: '1px solid var(--border)', borderRadius: 4 }} />
          </div>
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', marginBottom: 4 }}>Completed date</div>
            <input type="date" value={completed} onChange={(e) => setCompleted(e.target.value)}
              style={{ width: '100%', padding: '6px 10px', fontSize: 13, border: '1px solid var(--border)', borderRadius: 4 }} />
          </div>
        </div>

        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', marginBottom: 4 }}>Draw %</div>
          <input type="number" value={drawPct} onChange={(e) => setDrawPct(e.target.value)} step={0.1} min={0} max={100}
            style={{ width: '100%', padding: '6px 10px', fontSize: 13, border: '1px solid var(--border)', borderRadius: 4, fontVariantNumeric: 'tabular-nums' }} />
        </div>

        <div style={{ marginTop: 12 }}>
          <DocumentUploader
            value={null}
            onChange={(id) => { if (id) setPendingDocId(id); }}
            projectId={projectId}
            milestoneId={milestone.id}
            docType="milestone_evidence"
            label="Draw request / SF-425 / supporting evidence"
            hint="File is stored under this project + milestone."
          />
        </div>

        <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button onClick={save} disabled={saveMut.isPending}
            style={{ padding: '6px 14px', background: '#0f172a', color: '#fff', border: 'none', borderRadius: 4, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
            {saveMut.isPending ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>
    </div>
  );
}
